import React, { useCallback, useState } from 'react';

import { Root } from '@atlaskit/navigation-system/layout/root';
import { usePanelContext } from '@atlassian/entry-point-panel/use-panel-context';
import { AsyncRovoAgentCreateModal } from '@atlassian/rovo-agent-create-modal/async';

import { useRovoWorkspace } from '@townsquare/collab-context';
import { GoalCapabilitiesProvider } from '@townsquare/goal-capabilities';
import { GriffinLayout } from '@townsquare/griffin-layout';
import { GriffinTopNavigation } from '@townsquare/griffin-navigation';
import { GlobalAppShortcutsProvider } from '@townsquare/griffin-navigation/global-navigation-context';
import { ErrorBoundary } from '@townsquare/monitoring/error-boundary';
import { AppHomeWrapper } from '@townsquare/navigation/app-home-wrapper';
import { IntroPopup } from '@townsquare/onboarding/intro-popup';
import RovoAppificationChangeboarding from '@townsquare/post-office/message-components/rovo-appification-modal';
import { TownsquareGlobalPlacement } from '@townsquare/post-office/placements';
import { ProjectCapabilitiesProvider } from '@townsquare/project-capabilities';
import { useRovoAppification } from '@townsquare/rovo/appification';
import { SIDEBAR_PANEL } from '@townsquare/sidebar-panel/panels';
import { usePreloadedEntryPointPanel } from '@townsquare/sidebar-panel-utils/hooks/use-preloaded-entry-point-panel';
import { SiteBannerProvider } from '@townsquare/site-banner';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { expVal } from '@townsquare/stat-sig';
import { NAVX_UC_ML_POWERED_RELEVANCY_ENGINE } from '@townsquare/stat-sig/experiments';
import { fg } from '@townsquare/stat-sig/gate';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ProductHome } from '../navigation/ProductHome';
import { useUniversalCreateItems } from '../navigation/use-universal-create-items';

import { ConditionalSideNav, useDisableRovoSearchSideNav } from './ConditionalSideNav';
import { useIsOnRovoSearch } from './hooks';

type Props = {
	/**
	 * Prop to disable customization of the Home sidebar, for testing purposes.
	 */
	sidebarCustomizationDisabled?: boolean;
};

export function GriffinRoute(props: Props): React.JSX.Element {
	const { rovoAppificationState } = useRovoAppification();

	if (rovoAppificationState === 'loading') {
		return <FullScreenSpaLoader />;
	}

	return (
		<Root isSideNavShortcutEnabled>
			<SiteBannerProvider>
				<GlobalAppShortcutsProvider>
					<GriffinRouteComponent {...props} />
				</GlobalAppShortcutsProvider>
			</SiteBannerProvider>
		</Root>
	);
}

function GriffinRouteComponent({ sidebarCustomizationDisabled }: Props) {
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);
	const disableSideNavRovoSearch = useDisableRovoSearchSideNav();
	const showRovoSearchNavigationWhileLoading =
		useIsOnRovoSearch() && fg('rovo-search-team-eu-26-search-app');
	const loadingTopNav = showRovoSearchNavigationWhileLoading ? (
		<GriffinTopSectionLoading hideSideNav={disableSideNavRovoSearch} />
	) : null;

	return (
		<>
			<TownsquareGlobalPlacement />
			<RovoAppificationChangeboarding />
			<SidebarPanelContext>
				<GoalCapabilitiesProvider fallback={loadingTopNav}>
					<ProjectCapabilitiesProvider fallback={loadingTopNav}>
						<GriffinTopSection hideSideNav={disableSideNavRovoSearch} />
					</ProjectCapabilitiesProvider>
				</GoalCapabilitiesProvider>
				<ConditionalSideNav customizationDisabled={sidebarCustomizationDisabled} />
				<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
			</SidebarPanelContext>
			{noWorkspaceExperience && <IntroPopup />}
		</>
	);
}

const GriffinTopSection = ({ hideSideNav }: { hideSideNav?: boolean }) => {
	const rovoWorkspaceCloudId = useRovoWorkspace();
	const [isCreateAgentModalOpen, setIsCreateAgentModalOpen] = useState(false);
	const isDraftAgentCreationEnabled = fg('sltns-1529-draft-agent-non-consequential');
	const openCreateAgentModal = useCallback(() => setIsCreateAgentModalOpen(true), []);
	const closeCreateAgentModal = useCallback(() => setIsCreateAgentModalOpen(false), []);
	const {
		items: universalCreateItems,
		compact,
		isItemLoading,
		foldable,
	} = useUniversalCreateItems({
		onCreateAgent:
			isDraftAgentCreationEnabled && rovoWorkspaceCloudId ? openCreateAgentModal : undefined,
	});

	return (
		<>
			<GriffinTopNavigation
				renderProductHome={renderProductHome}
				universalCreateItems={universalCreateItems}
				compact={compact}
				enableCrossAppCreation={
					expVal(NAVX_UC_ML_POWERED_RELEVANCY_ENGINE, 'isEnabled', false)
						? 'personalizeWithGrowthRecommendations'
						: 'personalize'
				}
				hideSideNavToggleButton={hideSideNav}
				isItemLoading={isItemLoading}
				foldable={foldable}
			/>
			{isDraftAgentCreationEnabled && isCreateAgentModalOpen && rovoWorkspaceCloudId ? (
				<ErrorBoundary onError={closeCreateAgentModal} fallback={null}>
					<AsyncRovoAgentCreateModal
						cloudId={rovoWorkspaceCloudId}
						product="atlassian-studio"
						source="townsquare-universal-create-button"
						onModalClose={closeCreateAgentModal}
					/>
				</ErrorBoundary>
			) : null}
		</>
	);
};

const renderProductHome = () => (
	<AppHomeWrapper>
		<ProductHome />
	</AppHomeWrapper>
);

const GriffinTopSectionLoading = ({ hideSideNav }: { hideSideNav?: boolean }) => (
	<GriffinTopNavigation
		renderProductHome={renderProductHome}
		universalCreateItems={[]}
		hideSideNavToggleButton={hideSideNav}
		isItemLoading
	/>
);
